import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../../App";
import L from "leaflet";
import proj4 from "proj4";
import { logToServer } from "../../logger";

const TALUKAS = [
  "Dahanu",
  "Jawhar",
  "Mokhada",
  "Palghar",
  "Talasari",
  "Vasai",
  "Vikramgad",
  "Wada",
];

const CATEGORIES = [
  {
    id: "beaches",
    label: "Beaches",
    color: "#00E5FF",
    icon: "fa-umbrella-beach",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Beaches_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "mangroves",
    label: "Mangroves",
    color: "#69F0AE",
    icon: "fa-tree",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Mangroves_Taluka_Wise/${t}_clipped.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "water_bodies",
    label: "Water Bodies",
    color: "#40C4FF",
    icon: "fa-water",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Water_Bodies_Taluka_Wise/${t}_clipped.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "river",
    label: "River Network",
    color: "#00B0FF",
    icon: "fa-person-swimming",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/River_Net_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "buildings",
    label: "Buildings",
    color: "#FF6E40",
    icon: "fa-building",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Building_Taluka_Wise/${t}_clipped.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "road",
    label: "Road Network",
    color: "#FFEA00",
    icon: "fa-road",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Road_Net_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "railway",
    label: "Railway Network",
    color: "#E040FB",
    icon: "fa-train",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Railway_Net_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "transport",
    label: "Transport Stations",
    color: "#00E676",
    icon: "fa-bus",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Transport_Stations_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "taluka_hq",
    label: "Taluka HQ",
    color: "#FF1744",
    icon: "fa-map-pin",
    fetchFn: async (t) => {
      const res = await fetch(`/media/RegionNavigator/Taluka_HQ/Taluka_HQ.geojson`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const gj = await res.json();
      const features = gj.features
        .filter(
          (f) =>
            f.properties?.SUB_DIST &&
            f.properties.SUB_DIST.toLowerCase() === t.toLowerCase()
        )
        .map((f) => ({
          ...f,
          geometry: {
            type: "Point",
            coordinates: [f.properties.LONGITUDE, f.properties.LATITUDE],
          },
        }));
      return { type: "FeatureCollection", features };
    },
  },
  {
    id: "accommodation",
    label: "Accommodation",
    color: "#FF9100",
    icon: "fa-hotel",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Accomodation/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "admin_offices",
    label: "Admin Offices",
    color: "#2979FF",
    icon: "fa-landmark",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Administrative offices/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "atm",
    label: "ATM",
    color: "#D500F9",
    icon: "fa-credit-card",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/ATM/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "bank",
    label: "Bank",
    color: "#00BFA5",
    icon: "fa-building-columns",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Bank/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "college",
    label: "College",
    color: "#FF4081",
    icon: "fa-graduation-cap",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/College/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "eateries",
    label: "Eateries",
    color: "#FFAB40",
    icon: "fa-store",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Eateries/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "market",
    label: "Market",
    color: "#69F0AE",
    icon: "fa-utensils",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Market/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "medical",
    label: "Medical Facility",
    color: "#FF5252",
    icon: "fa-hospital",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Med_Facility/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "playschool",
    label: "Playschool",
    color: "#FFD740",
    icon: "fa-child",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Playschool/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "public_toilet",
    label: "Public Toilet",
    color: "#A0A0A0",
    icon: "fa-restroom",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Public_Toilet/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "school",
    label: "School",
    color: "#40C4FF",
    icon: "fa-school",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/School/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "water_well",
    label: "Water Well",
    color: "#18FFFF",
    icon: "fa-droplet",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Palghar_Utility/Water_Well/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
  {
    id: "religious",
    label: "Religious Places",
    color: "#FFD700",
    icon: "fa-place-of-worship",
    fetchFn: (t) =>
      fetch(`/media/RegionNavigator/Religious_places_Taluka_Wise/${t}.geojson`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
  },
];

// ── Tarapur radius zones ────────────────────────────────────────────────────
const RADII = [
{ id: "r5000", label: "5 km radius", file: "5000.geojson", color: "#E53935" },
{ id: "r16000", label: "16 km radius", file: "16000.geojson", color: "#FB8C00" },
{ id: "r18000", label: "18 km radius", file: "18000.geojson", color: "#FDD835" },
];

// ── UTM Zone 43N → WGS84 reprojection ──────────────────────────────────────
const UTM43N = "+proj=utm +zone=43 +datum=WGS84 +units=m +no_defs";
const WGS84  = "+proj=longlat +datum=WGS84 +no_defs";

function reprojectGeoJSON(gj) {
  return {
    ...gj,
    features: gj.features.map((f) => {
      const coords = f.geometry.coordinates;
      const reprojectRing = (ring) => ring.map(([x, y]) => proj4(UTM43N, WGS84, [x, y]));
      let geometry;
      if (f.geometry.type === "Polygon") {
        geometry = { type: "Polygon", coordinates: coords.map(reprojectRing) };
      } else if (f.geometry.type === "MultiPolygon") {
        geometry = { type: "MultiPolygon", coordinates: coords.map((poly) => poly.map(reprojectRing)) };
      } else if (f.geometry.type === "Point") {
        const [lng, lat] = proj4(UTM43N, WGS84, coords);
        geometry = { type: "Point", coordinates: [lng, lat] };
      } else {
        geometry = f.geometry;
      }
      return { ...f, geometry };
    }),
  };
}

function makePointIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="
      width:16px;height:16px;border-radius:50%;
      background:${color};border:2.5px solid rgba(255,255,255,0.95);
      box-shadow:0 0 8px ${color},0 0 3px rgba(0,0,0,0.8);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function getGeomType(gj) {
  return gj?.features?.[0]?.geometry?.type ?? "";
}
function RegionNavigator() {
  const { map, vis } = useContext(GlobalContext);

  const [panelOpen, setPanelOpen] = useState(false);
  const [persistLayers, setPersistLayers] = useState(false);
  const toolvisRef = useRef(null);

  const [selectedTaluka, setSelectedTaluka] = useState("");
  const [catStatus, setCatStatus] = useState({});
  const [activeCats, setActiveCats] = useState(new Set());

  // Tarapur
  const [tarapurActive, setTarapurActive] = useState(false);
  const tarapurLayerRef = useRef(null);

  // Radius box
  const [radiusBoxOpen, setRadiusBoxOpen] = useState(false);
  const [activeRadii, setActiveRadii] = useState(new Set());
  const radiusLayerRefs = useRef({});
  const radiusBoxRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [radiusBoxPosState, setRadiusBoxPosState] = useState({
    x: window.innerWidth / 2 - 90,
    y: 200,
  });

  const leftPanelRef = useRef(null);
  const leftPanelDragOffset = useRef({ x: 0, y: 0 });
  const [leftPanelPos, setLeftPanelPos] = useState({ x: 58, y: 96 });

  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

  // Leaflet layer refs
  const talukaLayerRef   = useRef(null);
  const districtLayerRef = useRef(null);
  const catLayerRefs     = useRef({});

  useEffect(() => {
    const handler = (e) => {
      if (
        !e.target.closest("#region-nav-btn") &&
        !e.target.closest("#region-nav-left-panel") &&
        toolvisRef.current &&
        !toolvisRef.current.contains(e.target)
      ) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);


  useEffect(() => () => removeAllLayers(), []);

  useEffect(() => {
    if (!map) return;
    if (panelOpen) {
      fetch("/media/RegionNavigator/Palghar_Outlines/Palghar_District.geojson")
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((gj) => {
          if (districtLayerRef.current) {
            try { districtLayerRef.current.remove(); } catch (_) {}
            districtLayerRef.current = null;
          }
          districtLayerRef.current = L.geoJSON(gj, {
            style: {
              color: "#FFFFFF",
              weight: 3,
              opacity: 1,
              fillColor: "#FFFFFF",
              fillOpacity: 0.06,
              dashArray: "10 6",
            },
          }).addTo(map);
          if (!selectedTaluka) {
            try {
              const b = districtLayerRef.current.getBounds();
              if (b.isValid()) map.flyToBounds(b, { padding: [30, 30], duration: 0.8, easeLinearity: 0.35 });
            } catch (_) {}
          }
          logToServer("info", "RegionNavigator: Palghar District outline loaded");
        })
        .catch((err) =>
          logToServer("warn", `RegionNavigator: district outline fetch failed — ${err.message}`)
        );
    } else {
      if (!persistLayers) {
        if (districtLayerRef.current) {
          try { districtLayerRef.current.remove(); } catch (_) {}
          districtLayerRef.current = null;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen, map]);

  useEffect(() => {
    if (!map) return;
    clearTalukaLayer();
    clearAllCatLayers();
    setActiveCats(new Set());
    setCatStatus({});

    if (!selectedTaluka) {
      if (districtLayerRef.current) {
        try {
          const b = districtLayerRef.current.getBounds();
          if (b.isValid()) map.flyToBounds(b, { padding: [30, 30], duration: 0.8, easeLinearity: 0.35 });
        } catch (_) {}
      }
      return;
    }

    const fetchJson = (url) =>
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });

    const zoomAndFit = (gj) => {
      clearTalukaLayer();
      talukaLayerRef.current = L.geoJSON(gj, {
        style: {
          color: "#CCFF00",
          weight: 2.5,
          opacity: 1,
          fillColor: "#CCFF00",
          fillOpacity: 0.05,
          dashArray: "6 4",
        },
      }).addTo(map);

      let targetBounds;
      try {
        targetBounds = talukaLayerRef.current.getBounds();
        if (!targetBounds.isValid()) targetBounds = null;
      } catch (_) {
        targetBounds = null;
      }
      if (!targetBounds) {
        logToServer("warn", `RegionNavigator: invalid bounds for ${selectedTaluka}`);
        return;
      }

      const currentZoom   = map.getZoom();
      const targetZoom    = map.getBoundsZoom(targetBounds, false, [40, 40]);
      const currentCenter = map.getCenter();
      const targetCenter  = targetBounds.getCenter();
      const distanceDeg   = Math.hypot(
        currentCenter.lat - targetCenter.lat,
        currentCenter.lng - targetCenter.lng
      );

      if (distanceDeg < 0.5 && Math.abs(currentZoom - targetZoom) < 2) {
        map.flyToBounds(targetBounds, { padding: [40, 40], duration: 0.9, easeLinearity: 0.35 });
      } else {
        const overviewZoom = Math.max(8, Math.min(currentZoom, targetZoom) - 1);
        map.flyTo(map.getCenter(), overviewZoom, { animate: true, duration: 0.45, easeLinearity: 0.5 });
        map.once("moveend", () => {
          if (!talukaLayerRef.current) return;
          map.flyToBounds(targetBounds, { padding: [40, 40], duration: 1.0, easeLinearity: 0.3 });
        });
      }
      logToServer("info", `RegionNavigator: animated zoom to ${selectedTaluka}`);
    };

    fetchJson(`/media/RegionNavigator/Tehsil_seperated/${selectedTaluka}.geojson`)
      .then(zoomAndFit)
      .catch(() =>
        logToServer("warn", `RegionNavigator: could not load boundary for ${selectedTaluka}`)
      );
  }, [selectedTaluka, map]);

  function clearTalukaLayer() {
    if (talukaLayerRef.current) {
      try { talukaLayerRef.current.remove(); } catch (_) {}
      talukaLayerRef.current = null;
    }
  }

  function clearAllCatLayers() {
    Object.values(catLayerRefs.current).forEach((l) => {
      try { l.remove(); } catch (_) {}
    });
    catLayerRefs.current = {};
  }

  function removeAllLayers() {
    clearTalukaLayer();
    clearAllCatLayers();
    if (districtLayerRef.current) {
      try { districtLayerRef.current.remove(); } catch (_) {}
      districtLayerRef.current = null;
    }
    if (tarapurLayerRef.current) {
      try { tarapurLayerRef.current.remove(); } catch (_) {}
      tarapurLayerRef.current = null;
    }
    Object.values(radiusLayerRefs.current).forEach((l) => {
      try { l.remove(); } catch (_) {}
    });
    radiusLayerRefs.current = {};
    setTarapurActive(false);
    setActiveRadii(new Set());
    setRadiusBoxOpen(false);
  }
  async function toggleTarapur() {
    if (tarapurActive) {
      if (tarapurLayerRef.current) {
        try { tarapurLayerRef.current.remove(); } catch (_) {}
        tarapurLayerRef.current = null;
      }
      setTarapurActive(false);
      clearAllRadiusLayers();
      return;
    }

    try {
      const res = await fetch("/media/RegionNavigator/Tarapur/Thermal_Plant_Location_projected.geojson");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const gj = await res.json();

      tarapurLayerRef.current = L.geoJSON(gj, {
        pointToLayer: (_, latlng) =>
          L.marker(latlng, {
            icon: L.divIcon({
              className: "",
              html: `<div style="
                width:24px;height:24px;border-radius:50%;
                background:#4CAF50;border:3px solid rgba(255,255,255,0.95);
                box-shadow:0 0 12px #4CAF50,0 0 4px rgba(0,0,0,0.8);
                display:flex;align-items:center;justify-content:center;
                font-size:13px;line-height:1;
              ">☢</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            }),
          }),
        onEachFeature: (feature, lyr) => {
          const p = feature.properties ?? {};
          const name = p.name ?? p.type ?? "Tarapur Thermal Power Plant";
          lyr.bindPopup(`
            <div style="font-family:sans-serif;min-width:150px;padding:0;border-radius:8px;overflow:hidden;">
              <div style="background:#4CAF50;padding:6px 10px;display:flex;align-items:center;gap:6px;">
                <span style="color:#000;font-size:11px;font-weight:700;letter-spacing:0.4px;text-transform:uppercase;">
                  ☢ Nuclear Plant
                </span>
              </div>
              <div style="padding:8px 10px;background:#fff;">
                <div style="font-size:13px;font-weight:600;color:#111;">${name}</div>
              </div>
            </div>
          `, { maxWidth: 220 });
        },
      }).addTo(map);

setTarapurActive(true);
      setRadiusBoxOpen(true);
      logToServer("info", "RegionNavigator: Tarapur nuclear plant loaded");

      // Auto-load 16 km radius by default
        const r16 = RADII.find((r) => r.id === "r16000");
           if (r16) toggleRadius(r16, true);
    } catch (err) {
      logToServer("error", `RegionNavigator: Tarapur fetch failed — ${err.message}`);
    }
  }

  async function toggleRadius(r, forceLoad = false) {
    if (!forceLoad && activeRadii.has(r.id)) {
      if (radiusLayerRefs.current[r.id]) {
        try { radiusLayerRefs.current[r.id].remove(); } catch (_) {}
        delete radiusLayerRefs.current[r.id];
      }
      setActiveRadii((prev) => {
        const next = new Set(prev);
        next.delete(r.id);
        return next;
      });
      return;
    }

    try {
      const res = await fetch(`/media/RegionNavigator/Tarapur/${r.file}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const gj = await res.json();
      // const reprojected = reprojectGeoJSON(gj);
      const reprojected = gj;

      const layer = L.geoJSON(reprojected, {
        style: {
          color: r.color,
          weight: 2,
          opacity: 0.9,
          fillColor: r.color,
          fillOpacity: 0.08,
          dashArray: "6 4",
        },
      }).addTo(map);

      radiusLayerRefs.current[r.id] = layer;
      setActiveRadii((prev) => new Set([...prev, r.id]));
      logToServer("info", `RegionNavigator: Tarapur radius ${r.id} loaded`);
    } catch (err) {
      logToServer("error", `RegionNavigator: radius ${r.id} failed — ${err.message}`);
    }
  }

    function closeRadiusBox() {
  setRadiusBoxOpen(false);
}

function clearAllRadiusLayers() {
  Object.values(radiusLayerRefs.current).forEach((l) => {
    try { l.remove(); } catch (_) {}
  });
  radiusLayerRefs.current = {};
  setActiveRadii(new Set());
  setRadiusBoxOpen(false);
}

  function startDrag(e) {
    dragOffset.current = {
      x: e.clientX - radiusBoxPosState.x,
      y: e.clientY - radiusBoxPosState.y,
    };
    const onMove = (ev) => {
      setRadiusBoxPosState({
        x: ev.clientX - dragOffset.current.x,
        y: ev.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

    function startLeftPanelDrag(e) {
    leftPanelDragOffset.current = {
      x: e.clientX - leftPanelPos.x,
      y: e.clientY - leftPanelPos.y,
    };
    const onMove = (ev) => {
      setLeftPanelPos({
        x: ev.clientX - leftPanelDragOffset.current.x,
        y: ev.clientY - leftPanelDragOffset.current.y,
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  async function toggleCategory(cat) {
    if (!selectedTaluka || !map) return;
    const isActive = activeCats.has(cat.id);

    if (isActive) {
      if (catLayerRefs.current[cat.id]) {
        try { catLayerRefs.current[cat.id].remove(); } catch (_) {}
        delete catLayerRefs.current[cat.id];
      }
      setActiveCats((prev) => {
        const next = new Set(prev);
        next.delete(cat.id);
        return next;
      });
      logToServer("info", `RegionNavigator: hidden ${cat.id} for ${selectedTaluka}`);
      return;
    }

    const currentStatus = catStatus[cat.id];
    if (currentStatus === "error" || currentStatus === "empty") return;

    setCatStatus((p) => ({ ...p, [cat.id]: "loading" }));

    try {
      const gj = await cat.fetchFn(selectedTaluka);
      if (!gj?.features?.length) {
        setCatStatus((p) => ({ ...p, [cat.id]: "empty" }));
        logToServer("info", `RegionNavigator: ${cat.id} has no features for ${selectedTaluka}`);
        return;
      }

      const geomType = getGeomType(gj);
      const isPoint  = geomType === "Point" || geomType === "MultiPoint";
      const isLine   = geomType === "LineString" || geomType === "MultiLineString";

      const layer = L.geoJSON(gj, {
        pointToLayer: isPoint
          ? (_, latlng) => L.marker(latlng, { icon: makePointIcon(cat.color) })
          : undefined,
        style: isPoint
          ? undefined
          : {
              color: cat.color,
              weight: isLine ? 2.5 : 1.5,
              opacity: 0.9,
              fillColor: cat.color,
              fillOpacity: isLine ? 0 : 0.18,
            },
        onEachFeature: (feature, lyr) => {
          const p = feature.properties ?? {};
          const name = p.name ?? p.type ?? "";
          const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
          lyr.bindPopup(`
            <div style="font-family:sans-serif;min-width:150px;padding:0;border-radius:8px;overflow:hidden;">
              <div style="background:${cat.color};padding:6px 10px;display:flex;align-items:center;gap:6px;">
                <i class="fa-solid ${cat.icon}" style="color:#000000;font-size:11px;"></i>
                <span style="color:#000000;font-size:11px;font-weight:700;letter-spacing:0.4px;text-transform:uppercase;">
                  ${cat.label}
                </span>
              </div>
              <div style="padding:8px 10px;background:#fff;">
                ${name ? `
                  <div style="font-size:13px;font-weight:600;color:#111;margin-bottom:${p.fclass ? "5px" : "0"};line-height:1.4;">
                    ${capitalize(name)}
                  </div>` : ""}
                ${p.fclass && p.name && p.name !== p.fclass ? `
                  <div style="display:flex;align-items:center;gap:5px;font-size:11px;">
                    <span style="background:${cat.color}22;color:#000000;border:1px solid ${cat.color}55;border-radius:4px;padding:1px 6px;font-size:10px;font-weight:600;">
                      ${capitalize(p.fclass)}
                    </span>
                  </div>` : ""}
                ${!name && !p.fclass ? `<div style="font-size:11px;color:#999;">No details available</div>` : ""}
              </div>
            </div>
          `, { maxWidth: 220 });
        },
      }).addTo(map);

      catLayerRefs.current[cat.id] = layer;
      setActiveCats((prev) => new Set([...prev, cat.id]));
      setCatStatus((p) => ({ ...p, [cat.id]: "ok" }));
      logToServer("info", `RegionNavigator: loaded ${cat.id} (${gj.features.length} features) for ${selectedTaluka}`);
    } catch (err) {
      setCatStatus((p) => ({ ...p, [cat.id]: "error" }));
      logToServer("error", `RegionNavigator: failed ${cat.id} — ${err.message}`);
    }
  }

  async function handleSelectAll() {
    for (const cat of CATEGORIES) {
      if (!activeCats.has(cat.id)) await toggleCategory(cat);
    }
  }

  function handleClearAll() {
    clearAllCatLayers();
    setActiveCats(new Set());
    setCatStatus((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (prev[id] === "error" || prev[id] === "empty") next[id] = prev[id];
      });
      return next;
    });
    logToServer("info", `RegionNavigator: cleared all layers for ${selectedTaluka}`);
  }

  function handleClose() {
    setPanelOpen(false);
    if (!persistLayers) {
      setSelectedTaluka("");
      setActiveCats(new Set());
      setCatStatus({});
      removeAllLayers();
      logToServer("info", "RegionNavigator: closed and reset (persist OFF)");
    } else {
      logToServer("info", "RegionNavigator: closed dropdown only (persist ON, layers retained)");
    }
  }

  function handlePersistToggle(e) {
    e.stopPropagation();
    setPersistLayers((prev) => {
      const next = !prev;
      logToServer("info", `RegionNavigator: persist layers → ${next}`);
      return next;
    });
  }

  const activeCatCount = activeCats.size;
  const showLeftPanel  = selectedTaluka && (panelOpen || persistLayers);
  
  return (
    <>
      {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}

     {showLeftPanel && (
      <div
        ref={leftPanelRef}
        id="region-nav-left-panel"
        style={{
          position: "fixed",
          top: leftPanelPos.y,      
          left: leftPanelPos.x,      
          width: "225px",
          maxHeight: leftPanelCollapsed ? "45px" : "calc(100vh - 130px)",
          transition: "max-height 0.25s ease, box-shadow 0.2s", 
          backgroundColor: "rgba(10,10,10,0.94)",
          backdropFilter: "blur(14px)",
          border: persistLayers
            ? "1px solid rgba(255,193,7,0.35)"
            : "1px solid rgba(255,255,255,0.09)",
          borderRadius: "10px",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: persistLayers
            ? "0 6px 28px rgba(255,193,7,0.12)"
            : "0 6px 28px rgba(0,0,0,0.55)",
        }}
      >

          {/* Header */}
          <div 
          onMouseDown={startLeftPanelDrag}
          style={{ 
            padding: "10px 12px 8px", 
            borderBottom: "1px solid rgba(255,255,255,0.07)", 
            flexShrink: 0,
            cursor: "grab",
            userSelect: "none",
            }}
            >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span style={{ color: "white", fontSize: "12px", fontWeight: 700 }}>{selectedTaluka}</span>
                  {persistLayers && (
                    <i className="fa-solid fa-lock" style={{ color: "#FFC107", fontSize: "9px" }} title="Layers are pinned" />
                  )}
                </div>
                <div style={{ color: "#555", fontSize: "10px", marginTop: "1px" }}>
                  {activeCatCount} / {CATEGORIES.length} layers active
                </div>
              </div>
              <div style={{ display: "flex", gap: "5px" }}>
                {activeCatCount > 0 && (
                  <button
                    onClick={handleClearAll}
                    style={{
                      background: "none",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#888",
                      fontSize: "10px",
                      borderRadius: "4px",
                      padding: "3px 7px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setLeftPanelCollapsed(p => !p)}
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#888",
                    fontSize: "10px",
                    borderRadius: "4px",
                    padding: "3px 7px",
                    cursor: "pointer",
                  }}
                >
      <i className={`fa-solid fa-chevron-${leftPanelCollapsed ? "down" : "up"}`} />
    </button>
              </div>
            </div>
            {/* Progress bar */}
            {!leftPanelCollapsed && (
            <div style={{ height: "3px", borderRadius: "2px", backgroundColor: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${(activeCatCount / CATEGORIES.length) * 100}%`,
                backgroundColor: "#378ADD",
                borderRadius: "2px",
                transition: "width 0.3s ease",
              }} />
            </div>
            )}
          </div>

          {/* Category rows */}
          <div style={{ overflowY: "auto", flex: 1, padding: "6px 8px 10px" }}>
            {CATEGORIES.map((cat) => {
              const active  = activeCats.has(cat.id);
              const status  = catStatus[cat.id] ?? "idle";
              const blocked = status === "error" || status === "empty";
              return (
                <div
                  key={cat.id}
                  onClick={() => !blocked && toggleCategory(cat)}
                  title={
                    status === "error" ? "File not found for this taluka"
                    : status === "empty" ? "No data available for this taluka"
                    : cat.label
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    marginBottom: "3px",
                    borderRadius: "6px",
                    border: active ? `1px solid ${cat.color}60` : "1px solid rgba(255,255,255,0.05)",
                    backgroundColor: active ? `${cat.color}1A` : "transparent",
                    cursor: blocked ? "not-allowed" : status === "loading" ? "wait" : "pointer",
                    transition: "all 0.15s",
                    userSelect: "none",
                    opacity: blocked ? 0.35 : 1,
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: active ? cat.color : "#2a2a2a",
                    boxShadow: active ? `0 0 6px ${cat.color}88` : "none",
                    transition: "all 0.15s",
                  }} />
                  <i className={`fa-solid ${cat.icon}`} style={{ fontSize: "11px", width: 13, textAlign: "center", flexShrink: 0, color: active ? cat.color : "#444", transition: "color 0.15s" }} />
                  <span style={{ flex: 1, fontSize: "11px", color: active ? "#fff" : "#777", transition: "color 0.15s" }}>
                    {cat.label}
                  </span>
                  {status === "loading" && (
                    <i className="fa-solid fa-spinner fa-spin" style={{ color: "#555", fontSize: "10px", flexShrink: 0 }} />
                  )}
                  {(status === "idle" || status === "empty") && (
                    <i className="fa-solid fa-eye-slash" style={{ color: "#333", fontSize: "10px", flexShrink: 0 }} title="Click to load this layer" />
                  )}
                  {status === "error" && (
                    <i className="fa-solid fa-eye-slash" style={{ color: "#c0392b", fontSize: "10px", flexShrink: 0 }} title="File not found for this taluka" />
                  )}
                  {active && status === "ok" && (
                    <i className="fa-solid fa-eye" style={{ color: cat.color, fontSize: "10px", flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: "8px 10px", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
            <button
              onClick={handleSelectAll}
              style={{
                width: "100%",
                padding: "6px 0",
                backgroundColor: "rgba(56,138,221,0.15)",
                border: "1px solid rgba(56,138,221,0.3)",
                color: "#378ADD",
                borderRadius: "6px",
                fontSize: "11px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <i className="fa-solid fa-layer-group" style={{ fontSize: "10px" }} />
              Show all available layers
            </button>
          </div>
        </div>
      )}

      {/* ── TOOLBAR BUTTON + RIGHT DROPDOWN ────────────────────────────────── */}
      <div style={{ position: vis ? "absolute" : "relative" }} className="toolscont">
        <button
          id="region-nav-btn"
          title="Palghar Navigator"
          className="btn text-white"
          onClick={() => setPanelOpen((p) => !p)}
          style={{
            zIndex: 1000,
            fontSize: "15px",
            backgroundColor: panelOpen ? "#185FA5" : "black",
            padding: "2px",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: panelOpen ? "2px solid #378ADD" : "none",
            transition: "background 0.2s",
            position: "relative",
          }}
        >
          <i className="fa-solid fa-arrow-right-to-city" />
          {activeCatCount > 0 && !panelOpen && (
            <span style={{
              position: "absolute",
              top: "-3px",
              right: "-3px",
              backgroundColor: "#378ADD",
              color: "white",
              fontSize: "9px",
              fontWeight: 700,
              width: 16,
              height: 16,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}>
              {activeCatCount}
            </span>
          )}
        </button>

        {/* Right dropdown panel */}
        {panelOpen && (
          <div
            ref={toolvisRef}
            className="toolsvis"
            id="region-nav-panel"
            style={{ width: "260px", padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "8px" }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ color: "white", fontSize: "13px", fontWeight: 600 }}>
                <i className="fa-solid fa-map-location-dot" style={{ marginRight: 7, color: "#378ADD", fontSize: "12px" }} />
                Palghar Navigator
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {/* Pin toggle */}
                <div
                  onClick={handlePersistToggle}
                  title={persistLayers ? "Layers pinned — click to unpin" : "Click to pin layers"}
                  style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", userSelect: "none" }}
                >
                  <span style={{ fontSize: "10px", color: persistLayers ? "#FFC107" : "#555" }}>Pin</span>
                  <div style={{
                    width: "28px", height: "15px", borderRadius: "999px",
                    backgroundColor: persistLayers ? "#FFC107" : "rgba(255,255,255,0.1)",
                    border: persistLayers ? "1px solid #FFC107" : "1px solid rgba(255,255,255,0.15)",
                    position: "relative", transition: "background 0.2s",
                  }}>
                    <div style={{
                      position: "absolute", top: "2px",
                      left: persistLayers ? "14px" : "2px",
                      width: "9px", height: "9px", borderRadius: "50%",
                      backgroundColor: persistLayers ? "#1a1a1a" : "#555",
                      transition: "left 0.2s",
                    }} />
                  </div>
                </div>
                <span onClick={handleClose} className="toolclose" style={{ cursor: "pointer", color: "white", fontSize: "18px", lineHeight: 1 }}>
                  &times;
                </span>
              </div>
            </div>

           {/* Tarapur Nuclear Plant — always visible */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "7px 10px",
                borderRadius: "6px",
                border: tarapurActive ? "1px solid #4CAF5060" : "1px solid rgba(255,255,255,0.08)",
                backgroundColor: tarapurActive ? "#4CAF5018" : "transparent",
                transition: "all 0.15s",
              }}
            >
              <div onClick={toggleTarapur} style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, cursor: "pointer" }}>
                <i className="fa-solid fa-radiation" style={{ color: tarapurActive ? "#4CAF50" : "#555", fontSize: "13px" }} />
                <span style={{ fontSize: "12px", color: tarapurActive ? "#fff" : "#c9c3c3", flex: 1 }}>
                  Tarapur Nuclear Plant
                </span>
              </div>
              {tarapurActive && (
                <>
                  <div
                    onClick={(e) => { e.stopPropagation(); setRadiusBoxOpen((prev) => !prev); }}
                    title={radiusBoxOpen ? "Hide radius box" : "Show radius box"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                      backgroundColor: radiusBoxOpen ? "rgba(76,175,80,0.25)" : "rgba(255,255,255,0.07)",
                      border: radiusBoxOpen ? "1px solid #4CAF5070" : "1px solid rgba(255,255,255,0.12)",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="fa-solid fa-circle-radiation"
                      style={{ color: radiusBoxOpen ? "#4CAF50" : "#666", fontSize: "10px" }}
                    />
                  </div>
                  {/* <i className="fa-solid fa-eye" style={{ color: "#4CAF50", fontSize: "10px" }} /> */}

                <i
                    className="fa-solid fa-eye"
                    style={{ color: "#4CAF50", fontSize: "10px", cursor: "pointer" }}
                    title="Hide Tarapur layer"
                    onClick={(e) => { e.stopPropagation(); toggleTarapur(); }}
                  />
                </>
              )}
            </div>

            {/* Taluka selector */}
            <div>
            
              <select
                value={selectedTaluka}
                onChange={(e) => setSelectedTaluka(e.target.value)}
                style={{
                  backgroundColor: "#111",
                  border: "1.5px solid rgba(255,255,255,0.2)",
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "6px",
                  padding: "6px 8px",
                  width: "100%",
                  marginTop: "4px",
                  outline: "none",
                  cursor: "pointer",
                  appearance: "auto",
                }}
              >
                <option value="" style={{ backgroundColor: "#111" }}>— Select taluka —</option>
                {TALUKAS.map((t) => (
                  <option key={t} value={t} style={{ backgroundColor: "#111", color: "white" }}>{t}</option>
                ))}
              </select>
            </div>


            {/* Taluka selected → hint + active badges */}
            {selectedTaluka ? (
              <>
                <div style={{
                  backgroundColor: persistLayers ? "rgba(255,193,7,0.07)" : "rgba(255,215,0,0.07)",
                  border: persistLayers ? "1px solid rgba(255,193,7,0.28)" : "1px solid rgba(255,215,0,0.18)",
                  borderRadius: "6px",
                  padding: "7px 10px",
                  fontSize: "11px",
                  color: "#aaa",
                  display: "flex",
                  gap: "7px",
                  alignItems: "flex-start",
                }}>
                  <i
                    className={`fa-solid ${persistLayers ? "fa-lock" : "fa-circle-info"}`}
                    style={{ color: persistLayers ? "#FFC107" : "#FFD700", marginTop: "1px", flexShrink: 0, fontSize: "11px" }}
                  />
                  <span>
                    {persistLayers ? (
                      <>Layers are <strong style={{ color: "#FFC107" }}>pinned</strong>.</>
                    ) : (
                      <>Toggle layers from the <strong style={{ color: "white" }}>left panel</strong>.</>
                    )}
                  </span>
                </div>

                {activeCatCount > 0 && (
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "8px" }}>
                    <div style={{ color: "#555", fontSize: "10px", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Active layers ({activeCatCount})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {CATEGORIES.filter((c) => activeCats.has(c.id)).map((c) => (
                        <span
                          key={c.id}
                          onClick={() => toggleCategory(c)}
                          title={`Click to hide ${c.label}`}
                          style={{
                            backgroundColor: `${c.color}20`,
                            border: `1px solid ${c.color}55`,
                            color: c.color,
                            fontSize: "10px",
                            padding: "2px 7px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            transition: "opacity 0.15s",
                          }}
                        >
                          <i className={`fa-solid ${c.icon}`} style={{ fontSize: "9px" }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: "#444", fontSize: "11px", textAlign: "center", padding: "4px 0" }}>
                Select a taluka to explore its data layers.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── DRAGGABLE RADIUS BOX ────────────────────────────────────────────── */}
      {radiusBoxOpen && (
        <div
          ref={radiusBoxRef}
          style={{
            position: "fixed",
            left: radiusBoxPosState.x,
            top: radiusBoxPosState.y,
            width: "180px",
            backgroundColor: "rgba(10,10,10,0.94)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: "10px",
            zIndex: 2000,
            boxShadow: "0 6px 28px rgba(0,0,0,0.55)",
            overflow: "hidden",
          }}
        >
          {/* Drag handle header */}
          <div
            onMouseDown={startDrag}
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "grab",
              userSelect: "none",
            }}
          >
            <span style={{ color: "white", fontSize: "11px", fontWeight: 700 }}>☢ Tarapur Radius</span>
            <span onClick={closeRadiusBox} style={{ color: "#888", fontSize: "16px", cursor: "pointer", lineHeight: 1 }}>
              &times;
            </span>
          </div>

          {/* Radius options */}
          <div style={{ padding: "6px 8px 8px" }}>
            {RADII.map((r) => {
              const active = activeRadii.has(r.id);
              return (
                <div
                  key={r.id}
                  onClick={() => toggleRadius(r)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    marginBottom: "3px",
                    borderRadius: "6px",
                    border: active ? `1px solid ${r.color}60` : "1px solid rgba(255,255,255,0.05)",
                    backgroundColor: active ? `${r.color}1A` : "transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    userSelect: "none",
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: active ? r.color : "#2a2a2a",
                    boxShadow: active ? `0 0 6px ${r.color}88` : "none",
                    transition: "all 0.15s",
                  }} />
                  <span style={{ fontSize: "11px", color: active ? "#fff" : "#777", flex: 1 }}>
                    {r.label}
                  </span>
                  {active && (
                    <i className="fa-solid fa-eye" style={{ color: r.color, fontSize: "10px", flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

export default RegionNavigator;
